import { create } from "zustand";
import { persist } from "zustand/middleware";

interface AppState {
  apiToken: string | null;
  apiUrl: string;
  selectedSessionId: string | null;
  selectedSkill: string;
  // Backend may run either OpenAI (legacy) or Claude (Claude SDK). Keep this flexible.
  selectedModel: string;
  setApiToken: (token: string | null) => void;
  setApiUrl: (url: string) => void;
  setSelectedSessionId: (sessionId: string | null) => void;
  setSelectedSkill: (skill: string) => void;
  setSelectedModel: (model: string) => void;
  initializeFromUrl: () => void;
}

// Extend Window interface for runtime config injection
declare global {
  interface Window {
    __PLUMCOT_API_URL__?: string;
  }
}

// Get API URL from runtime config (for Docker) or build-time env var
const getDefaultApiUrl = () => {
  // Check for runtime config injected by Docker entrypoint
  if (typeof window !== "undefined" && window.__PLUMCOT_API_URL__) {
    return window.__PLUMCOT_API_URL__;
  }
  // Fall back to build-time env var or default
  return import.meta.env.VITE_PLUMCOT_API_URL || "http://localhost:8080";
};

// URL parameter name for session ID
const SESSION_PARAM = "session";

// Update URL with session ID (without page reload)
const updateUrlWithSession = (sessionId: string | null) => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  if (sessionId) {
    url.searchParams.set(SESSION_PARAM, sessionId);
  } else {
    url.searchParams.delete(SESSION_PARAM);
  }

  // Update URL without reloading the page
  window.history.replaceState({}, "", url.toString());
};

// Get session ID from URL
const getSessionFromUrl = (): string | null => {
  if (typeof window === "undefined") return null;

  const params = new URLSearchParams(window.location.search);
  return params.get(SESSION_PARAM);
};

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      apiToken: null,
      apiUrl: getDefaultApiUrl(),
      selectedSessionId: null,
      selectedSkill: "/general",
      selectedModel: "gpt-4o-mini",
      setApiToken: (token) => set({ apiToken: token }),
      setApiUrl: (url) => set({ apiUrl: url }),
      setSelectedSessionId: (sessionId) => {
        set({ selectedSessionId: sessionId });
        updateUrlWithSession(sessionId);
      },
      setSelectedSkill: (skill) => set({ selectedSkill: skill }),
      setSelectedModel: (model) => set({ selectedModel: model }),
      initializeFromUrl: () => {
        // Get session ID from URL on initial load
        const sessionFromUrl = getSessionFromUrl();
        if (sessionFromUrl && sessionFromUrl !== get().selectedSessionId) {
          set({ selectedSessionId: sessionFromUrl });
        }

        // Listen for browser back/forward navigation
        if (typeof window !== "undefined") {
          window.addEventListener("popstate", () => {
            const sessionFromUrl = getSessionFromUrl();
            if (sessionFromUrl !== get().selectedSessionId) {
              set({ selectedSessionId: sessionFromUrl });
            }
          });
        }
      },
    }),
    {
      name: "plumcot-ui-storage",
    }
  )
);

